import { Box } from "@similarweb/ui-components/foundations";

import { iconNamePerSignalType } from "app/pages/sales-intelligence/modules/company-analysis/constants/signalsIcons";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { useCompanyBaseInfoQuery } from "app/pages/sales-intelligence/modules/company-analysis/queries/useCompanyBaseInfoQuery";
import signalEventsService from "app/pages/sales-intelligence/modules/company-analysis/services/signals/signalEventsService";
import type { SignalEventDto } from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import { translate } from "app/filters/i18nFilter";
import SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";

import SignalEventInfo from "../SignalEventInfo/SignalEventInfo";
import SignalTitleWithCountryIcon from "../SignalTitleWithCountryIcon/SignalTitleWithCountryIcon";

import {
    StyledOpenNewWindowIcon,
    StyledSignalDate,
    StyledSignalFooter,
    StyledSignalSubType,
    StyledSignalTag,
    StyledSignalTitle,
    StyledSignalType,
    StyledSignalTypeIcon,
    StyledSignalWrapper,
} from "./styles";

type SignalEventListItemProps = {
    signal: SignalEventDto;
    onArticleLinkClick: (signalEvent: SignalEventDto) => void;
    onCompanyLinkClick: (signalEvent: SignalEventDto) => void;
    onShowDescriptionClick: () => void;
};

const SignalEventListItem = ({
    signal,
    onArticleLinkClick,
    onCompanyLinkClick,
    onShowDescriptionClick,
}: SignalEventListItemProps) => {
    const { params } = useCurrentStateAndParamsWithFixedDomain();

    const { data: companyInfo } = useCompanyBaseInfoQuery(params.key);
    const { eventHappenedDaysAgo, type } = signal;

    const title = signalEventsService.getSignalEventTitle(signal, companyInfo);
    const country = signalEventsService.getSignalEventCountry(signal);

    const tags = signalEventsService.getSignalEventTags(signal);

    const handleArticleLinkClick = () => onArticleLinkClick(signal);

    return (
        <StyledSignalWrapper
            data-automation="signal-event-item"
            data-automation-type={type}
        >
            <Box padding="0 78px 0 32px">
                <StyledSignalType>
                    <StyledSignalTypeIcon
                        iconName={iconNamePerSignalType[type]}
                        size="xs"
                        data-automation="signal-event-icon"
                    />
                    <StyledSignalSubType data-automation="signal-event-subtype">
                        {translate(`si.signals.type.${type}.text`)}
                    </StyledSignalSubType>
                </StyledSignalType>
                <StyledSignalTitle
                    isCapitalized={type !== SignalType.Traffic}
                    data-automation="signal-event-title"
                >
                    <SignalTitleWithCountryIcon
                        country={country}
                        title={title}
                    />

                    {type === SignalType.News && signal.url ? (
                        <a
                            href={signal.url}
                            target="_blank"
                            rel="noreferrer"
                            onMouseDown={handleArticleLinkClick}
                            data-automation="signal-event-news-link"
                        >
                            <StyledOpenNewWindowIcon
                                iconName="open-new-window"
                                size="xs"
                            />
                        </a>
                    ) : null}
                </StyledSignalTitle>
                <SignalEventInfo
                    signal={signal}
                    companyInfo={companyInfo}
                    onCompanyLinkClick={onCompanyLinkClick}
                    onShowDescriptionClick={onShowDescriptionClick}
                />
                <StyledSignalFooter>
                    <StyledSignalDate data-automation="signal-event-date">
                        {signalEventsService.getSignalEventDate(eventHappenedDaysAgo)}
                    </StyledSignalDate>
                    {tags.map((tag) => (
                        <StyledSignalTag key={tag}>{tag}</StyledSignalTag>
                    ))}
                </StyledSignalFooter>
            </Box>
        </StyledSignalWrapper>
    );
};

export default SignalEventListItem;
